import {
    ApiResponse,
    type AttachLabelResponse,
    type Image,
    type LastScannedProduct,
    type LabelInfoResponse,
    type LabelRegisterResponse,
    type Product,
    type ProductListResponse,
    type ProductScanRecognitionResponse,
    type ScanShape,
} from '@/types'

export interface RequestConfig {
    headers?: Record<string, string>
    timeout?: number
    signal?: AbortSignal
}

export interface ApiError {
    code: number
    message: string
    [key: string]: unknown
}

type RawImage = Image & {
    original?: string
    url?: string
}

type ProductPayload = Product & {
    bg_image?: RawImage | null
    product_image?: RawImage | null
}

type ProductScanRecognitionPayload = Omit<ProductScanRecognitionResponse, 'product'> & {
    product: ProductPayload
}

const frontendEnv =
    (
        import.meta as ImportMeta & {
            env?: Record<string, string | boolean | undefined>
        }
    ).env ?? {}

function resolveBackendOrigin() {
    const apiBase =
        typeof frontendEnv.VITE_API_BASE_URL === 'string' ? frontendEnv.VITE_API_BASE_URL : ''

    if (/^https?:\/\//.test(apiBase)) {
        return new URL(apiBase).origin
    }

    if (frontendEnv.DEV) {
        return 'http://localhost:3000'
    }

    if (typeof window !== 'undefined') {
        return window.location.origin
    }

    return ''
}

function resolveUploadsBaseUrl() {
    const explicit =
        typeof frontendEnv.VITE_UPLOADS_BASE_URL === 'string'
            ? frontendEnv.VITE_UPLOADS_BASE_URL.trim()
            : ''

    if (explicit) {
        return explicit.replace(/\/+$/, '')
    }

    return `${resolveBackendOrigin().replace(/\/+$/, '')}/uploads`
}

function buildImageUrl(image: RawImage) {
    const directUrl = image.url?.trim()
    if (directUrl) {
        return directUrl
    }

    const original = image.original?.trim()
    if (!original) {
        return undefined
    }

    if (/^(https?:)?\/\//.test(original) || original.startsWith('data:')) {
        return original
    }

    const backendOrigin = resolveBackendOrigin()
    if (original.startsWith('/uploads/')) {
        return `${backendOrigin}${original}`
    }

    const normalizedPath = original.replace(/^\/+/, '').replace(/^uploads\//, '')
    return `${resolveUploadsBaseUrl()}/${normalizedPath}`
}

function normalizeImage(rawImage: RawImage | null | undefined): Image | null {
    if (!rawImage) {
        return null
    }

    return {
        ...rawImage,
        url: buildImageUrl(rawImage),
    }
}

export function normalizeProduct(rawProduct: ProductPayload): Product {
    return {
        ...rawProduct,
        bg_image: normalizeImage(rawProduct.bg_image),
        product_image: normalizeImage(rawProduct.product_image),
    }
}

function normalizeOptionalString(value: unknown): string | undefined {
    return typeof value === 'string' && value ? value : undefined
}

function normalizeOptionalNumber(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function normalizeProductScanRecognition(
    payload: ProductScanRecognitionPayload
): ProductScanRecognitionResponse {
    return {
        ...payload,
        product: normalizeProduct(payload.product),
    }
}

export function normalizeLastScannedProduct(payload: unknown): LastScannedProduct | null {
    if (!payload || typeof payload !== 'object') {
        return null
    }

    const data = payload as Record<string, unknown>

    if (data.product && typeof data.product === 'object') {
        const label =
            data.label && typeof data.label === 'object'
                ? (data.label as Record<string, unknown>)
                : null

        return {
            product: normalizeProduct(data.product as ProductPayload),
            scanned_at:
                typeof data.scanned_at === 'string'
                    ? data.scanned_at
                    : typeof data.created_at === 'string'
                      ? data.created_at
                      : new Date().toISOString(),
            label_id:
                normalizeOptionalString(data.label_id) ?? normalizeOptionalString(label?.label_id),
            uniq_label_id:
                normalizeOptionalString(data.uniq_label_id) ??
                normalizeOptionalString(label?.uniq_label_id),
            label_number:
                normalizeOptionalNumber(data.label_number) ??
                normalizeOptionalNumber(label?.number),
            product_total:
                normalizeOptionalNumber(data.product_total) ??
                normalizeOptionalNumber(label?.total),
        }
    }

    if (typeof data.name === 'string') {
        return {
            product: normalizeProduct(data as unknown as ProductPayload),
            scanned_at:
                typeof data.created_at === 'string' ? data.created_at : new Date().toISOString(),
            label_id: normalizeOptionalString(data.label_id),
            uniq_label_id: normalizeOptionalString(data.uniq_label_id),
            label_number: normalizeOptionalNumber(data.label_number),
            product_total: normalizeOptionalNumber(data.product_total),
        }
    }

    return null
}

export class ApiService {
    private baseURL: string
    private defaultTimeout: number = 5000

    constructor(baseURL: string = import.meta.env?.VITE_API_BASE_URL || '/api/v1') {
        this.baseURL = baseURL
    }

    private async makeRequest<T>(
        endpoint: string,
        options: RequestInit = {},
        config?: RequestConfig
    ): Promise<ApiResponse<T>> {
        const url = `${this.baseURL}${endpoint}`

        const isFormData = options.body instanceof FormData
        const headers = isFormData
            ? { ...(config?.headers || {}), ...((options.headers as Record<string, string>) || {}) }
            : {
                  'Content-Type': 'application/json',
                  ...(config?.headers || {}),
                  ...((options.headers as Record<string, string>) || {}),
              }

        const controller = new AbortController()
        const timeoutId = setTimeout(
            () => controller.abort(),
            config?.timeout || this.defaultTimeout
        )

        try {
            const response = await fetch(url, {
                ...options,
                headers,
                signal: config?.signal || controller.signal,
            })

            clearTimeout(timeoutId)

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                return {
                    success: false,
                    error: {
                        ...errorData,
                        code: response.status,
                        message:
                            errorData.detail ||
                            errorData.title ||
                            errorData.error?.message ||
                            errorData.message ||
                            `HTTP ${response.status}`,
                    },
                }
            }

            const data = await response.json()
            return { success: true, data }
        } catch (error) {
            clearTimeout(timeoutId)

            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    return { success: false, error: { code: 0, message: 'Request timeout' } }
                }
                return {
                    success: false,
                    error: { code: -1, message: error.message || 'Network error' },
                }
            }

            return { success: false, error: { code: -1, message: 'Unknown error occurred' } }
        }
    }

    async upload<T>(
        url: string,
        formData: FormData,
        config?: RequestConfig
    ): Promise<ApiResponse<T>> {
        return this.makeRequest<T>(url, { method: 'POST', body: formData }, config)
    }

    async getProducts(params?: {
        page?: number
        limit?: number
        q?: string
    }): Promise<ApiResponse<ProductListResponse>> {
        const query = new URLSearchParams()
        if (params?.page) query.set('page', String(params.page))
        if (params?.limit) query.set('limit', String(params.limit))
        if (params?.q) query.set('q', params.q)
        const qs = query.toString()
        return this.makeRequest<ProductListResponse>(`/products${qs ? `?${qs}` : ''}`)
    }

    async registerLabel(
        imageFile: File,
        shape?: ScanShape
    ): Promise<ApiResponse<LabelRegisterResponse>> {
        const formData = new FormData()
        formData.append('image', imageFile)
        if (shape) {
            formData.append('shape', shape)
        }
        return this.upload<LabelRegisterResponse>('/labels/register', formData, { timeout: 30000 })
    }

    async getLabelInfo(uniqLabelId: string): Promise<ApiResponse<LabelInfoResponse>> {
        return this.makeRequest<LabelInfoResponse>(`/labels/${encodeURIComponent(uniqLabelId)}`)
    }

    async attachLabelToProduct(
        productId: string,
        uniqLabelId: string
    ): Promise<ApiResponse<AttachLabelResponse>> {
        return this.makeRequest<AttachLabelResponse>(`/products/${productId}/labels`, {
            method: 'POST',
            body: JSON.stringify({ uniq_label_id: uniqLabelId }),
        })
    }

    async recognizeProduct(
        imageFile: File,
        shape?: ScanShape
    ): Promise<ApiResponse<ProductScanRecognitionResponse>> {
        const formData = new FormData()
        formData.append('image', imageFile)
        if (shape) {
            formData.append('shape', shape)
        }
        const response = await this.upload<ProductScanRecognitionPayload>(
            '/product-scans/recognize',
            formData,
            {
                timeout: 30000,
            }
        )

        if (!response.success || !response.data) {
            return response as ApiResponse<ProductScanRecognitionResponse>
        }

        return {
            success: true,
            data: normalizeProductScanRecognition(response.data),
        }
    }
}

export const apiService = new ApiService()
